// companyStore.js - 全体を丸ごとコピペできるように実装

import { defineStore } from 'pinia'
import { apiService } from '@/services/api'

export const useCompanyStore = defineStore('company', {
  state: () => ({
    companies: [],
    tableList: [],
    formData: null,
    formInit: {
      id: null,
      uuid: '',
      rowNumber: 0,
      name: '',
      nameKana: '',
      corporateNumber: '',
      industry: '',
      employeeCount: 0,
      postalCode: ['', ''],
      prefecture: '',
      prefectureKana: '',
      prefectureCode: '',
      address: '',
      addressKana: '',
      representativeTitle: '',
      representativeTitleKana: '',
      representativeLastName: '',
      representativeFirstName: '',
      representativeLastKana: '',
      representativeFirstKana: '',
      phone: ['', '', ''],
      fax: ['', '', ''],
      healthInsurance: '',
      memo: '',
    },
    selectedCompany: null,
    settlementTerms: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
    currentCompanyName: '',
    // 追加: 事業場一覧を保持するステート
    branches: [],
    // 追加: 事業場ごとの労災保険区分マップを保持するステート
    branchInsuranceTypesMap: {}, // { branchId: { typeId: typeName, ... }, ... }
  }),
  actions: {
    // テーブル表示用のリストを初期化
    initTableList() {
      this.tableList = [
        { label: 'No', key: 'rowNumber', data: (item, value) => value },
        { label: '会社名', key: 'name', data: (item, value) => value },
        { label: '都道府県', key: 'prefecture', data: (item, value) => value },
        {
          label: '業種',
          key: 'industry',
          data: (item, value) => {
            return value?.length > 10 ? value.substring(0, 20) + '...' : value
          },
        },
        { label: '従業員数', key: 'employeeCount', data: (item, value) => value || 0 },
        {
          label: '事業場',
          key: 'branch',
          data: (item, value) => '事業場一覧へ',
          action: (table, item, event) => {
            event.stopPropagation()
            this.setSelectedCompany(item.id)
          },
          actionEnabled: (table, item) => true, // 常に有効
          css: 'btn',
        },
        {
          label: '従業員',
          key: 'staff',
          data: (item, value) => '従業員一覧へ',
          action: (table, item, event) => {
            event.stopPropagation()
            this.setSelectedCompany(item.id)
          },
          actionEnabled: (table, item) => true, // 常に有効
          css: 'btn',
        },
        {
          label: 'ストレージ',
          key: 'storage',
          data: (item, value) => 'ストレージへ',
          action: (table, item, event) => {
            event.stopPropagation()
            this.setSelectedCompany(item.id)
          },
          actionEnabled: (table, item) => true, // 常に有効
          css: 'btn',
        },
        {
          label: 'メモ',
          key: 'memo',
          data: (item, value) => {
            return value?.length > 20 ? value.substring(0, 20) + '...' : value
          },
        },
      ]
    },

    // フォームデータの初期化
    async setFormData(id) {
      if (id) {
        // 編集の場合
        const company = await this.getCompany(id)
        if (company) {
          this.formData = JSON.parse(JSON.stringify(company))
          return
        }
      }
      // 新規作成の場合
      this.clearFormData()
    },

    // 会社一覧を取得
    async getCompanies() {
      try {
        const response = await apiService.get('/api/companies')
        this.companies = response.companies
        return true
      } catch (error) {
        return false
      }
    },

    // 会社の情報を取得
    async getCompany(id) {
      try {
        const response = await apiService.get(`/api/companies/${id}`)
        return response.company
      } catch (error) {
        return false
      }
    },

    // 会社情報を保存（新規作成・更新）
    async saveCompany() {
      try {
        const response = await apiService.post('/api/companies', this.formData)
        await this.getCompanies()
        return response.message
      } catch (error) {
        return null
      }
    },

    // 会社を削除
    async deleteCompany() {
      try {
        const response = await apiService.delete(`/api/companies/${this.formData.id}`)
        await this.getCompanies()
        return response.message
      } catch (error) {
        return null
      }
    },

    // 会社IDから会社名だけを取得する関数
    async getCompanyNameById(id) {
      try {
        // 専用APIから会社名だけを取得
        const response = await apiService.get(`/api/companies/${id}/name`)

        // APIレスポンスから会社名を取得（APIの形式に合わせて調整）
        const name = response.name || '会社名未設定'

        // 情報を更新
        this.currentCompanyName = name

        return name
      } catch (error) {
        return '会社名未設定'
      }
    },

    // IDで会社を検索
    getCompanyById(id) {
      return this.companies.find(company => company.id === Number(id))
    },

    // 会社情報をコピー（新規作成時の雛形として）
    copyCompany() {
      this.formData.id = null
    },

    // フォームデータをクリア
    clearFormData() {
      this.formData = JSON.parse(JSON.stringify(this.formInit))
    },

    // 郵便番号が入力されているかチェック
    isPostalCodeFilled() {
      // 0番目が3桁、1番目が4桁の配列であることを確認
      return this.formData.postalCode[0].length === 3 && this.formData.postalCode[1].length === 4
    },

    // 住所をセット
    setAddress(address) {
      if (!address) {
        return
      }
      this.formData.prefecture = address.prefecture
      this.formData.address = address.address1
      this.formData.prefectureKana = address.prefectureKana
      // this.formData.addressKana = address.address1Kana
      // 半角数字を全角数字に変換
      if (address.address1Kana) {
        this.formData.addressKana = address.address1Kana.replace(/[0-9]/g, function (s) {
          return String.fromCharCode(s.charCodeAt(0) + 0xfee0)
        })
      } else {
        this.formData.addressKana = address.address1Kana
      }
    },

    // 現在の会社情報をとっておく
    setSelectedCompany(id) {
      this.selectedCompany = this.companies.find(company => company.id === Number(id))
    },

    // 会社IDに紐づく事業場一覧を取得
    async getBranches(companyId) {
      try {
        const response = await apiService.get(`/api/companies/${companyId}/branches`)
        this.branches = response.branches
        return this.branches
      } catch (error) {
        return false
      }
    },

    // 会社IDに紐づく全事業場の労災保険区分を一度に取得
    async getAllLaborInsuranceTypes(companyId) {
      try {
        // 会社IDに紐づく全事業場の労災保険区分を取得するAPIを呼び出す
        const response = await apiService.get(`/api/companies/${companyId}/labor-insurance-types`)

        // レスポンスの形式: { branchId: { typeId: typeName, ... }, ... }
        this.branchInsuranceTypesMap = response.laborInsuranceTypes
        return this.branchInsuranceTypesMap
      } catch (error) {
        return false
      }
    },
  },
  getters: {
    postalCodeWithHyphen(state) {
      return this.formData ? this.formData.postalCode[0] + '-' + this.formData.postalCode[1] : ''
    },

    // 事業場の選択肢をフォーマットして返すゲッター
    branchOptions(state) {
      return state.branches.map(branch => ({
        label: branch.name,
        value: branch.id,
      }))
    },

    // 特定の事業場IDに対応する労災保険区分の選択肢を返すゲッター
    laborInsuranceTypeOptions: state => branchId => {
      if (!branchId || !state.branchInsuranceTypesMap[branchId]) {
        return []
      }

      const insuranceTypes = state.branchInsuranceTypesMap[branchId]
      return Object.entries(insuranceTypes).map(([value, label]) => ({
        label,
        value,
      }))
    },
  },
})
